import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import PlaybookCaptureForm from "../components/homepage/PlaybookCaptureForm";
import {
  MENU_TRAINING_CHECKLIST_PATH,
  MENU_TRAINING_PILLAR_PATH,
  MENU_TRAINING_TEMPLATE_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const canonicalUrl = `https://speakyourmenu.com${MENU_TRAINING_TEMPLATE_PATH}`;
const guideUrl = `https://speakyourmenu.com${MENU_TRAINING_PILLAR_PATH}`;
const checklistUrl = `https://speakyourmenu.com${MENU_TRAINING_CHECKLIST_PATH}`;
const pageTitle = "Menu Training Template for Restaurants | Speak Your Menu";
const pageDescription = "Get the free Menu Training Template for restaurants and use it to tighten onboarding, daily reinforcement, and manager follow-through.";

const templateHighlights = [
  "A practical structure for translating menu knowledge into repeatable training habits.",
  "A shorter operating layer you can use alongside onboarding and pre-shift reinforcement.",
  "A menu-training asset built to support consistency, retention, and clearer manager follow-through.",
];

const useCases = [
  {
    title: "Opening a cleaner onboarding flow",
    body: "Use the template when new hires need a clearer starting structure than shadow shifts and verbal handoff alone.",
  },
  {
    title: "Reinforcing knowledge after orientation",
    body: "Use it to keep ingredients, allergens, and guest-facing language from fading after the first few shifts.",
  },
  {
    title: "Giving managers a repeatable standard",
    body: "Use it when coaching is happening unevenly across shifts and you need a more stable operating baseline.",
  },
];

const rolloutSteps = [
  "Start with the full menu training guide so the team is aligned on the larger training system.",
  "Use the template to translate that system into a practical operating format your managers can apply weekly.",
  "Pair it with the checklist when you want the shortest execution layer for reinforcement and follow-through.",
];

export default function MenuTrainingTemplatePage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Menu Training Program", item: guideUrl },
      { "@type": "ListItem", position: 3, name: "Menu Training Template", item: canonicalUrl },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Menu Training Template for Restaurants",
    description: pageDescription,
    author: { "@type": "Organization", name: "Speak Your Menu" },
    publisher: { "@type": "Organization", name: "Speak Your Menu" },
    mainEntityOfPage: canonicalUrl,
    dateModified: new Date().toISOString(),
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell resource-grid-two resource-grid-two-stackable">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Free training template</span>
              <h1 className="homepage-hero-title resource-hero-title">Get the free Menu Training Template.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                This PDF gives restaurant operators a practical template for tightening onboarding, daily reinforcement, and manager follow-through around menu knowledge.
              </p>
              <div className="resource-inline-actions">
                <Link to={MENU_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                <Link to={MENU_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open checklist</Link>
              </div>
            </div>
            <aside className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Email delivery</span>
              <h2 className="homepage-card-title resource-aside-title">Send the template to your inbox</h2>
              <p className="homepage-card-copy">Enter your work email and we will send the Menu Training Template as a PDF attachment.</p>
              <PlaybookCaptureForm
                source="menu_training_template_page"
                buttonLabel="Email me the template."
                helpText="We’ll email the Menu Training Template PDF as an attachment."
              />
            </aside>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">What it does</span>
              <h2 className="homepage-section-title resource-section-title">What this template is for</h2>
              <ul className="resource-bullet-list">
                {templateHighlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="homepage-surface-card resource-card resource-card-contrast">
              <span className="homepage-label-chip">Best fit</span>
              <h2 className="homepage-card-title resource-aside-title">Use it when the training system exists, but execution keeps drifting.</h2>
              <p className="homepage-card-copy">
                The template is meant to shorten the gap between the full strategy and the weekly operating rhythm your managers actually need to maintain.
              </p>
            </article>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt">
          <div className="homepage-shell">
            <span className="homepage-label-chip">Use cases</span>
            <h2 className="homepage-section-title resource-section-title">Three situations where the template helps most</h2>
            <div className="resource-step-grid">
              {useCases.map((item, index) => (
                <article key={item.title} className="homepage-surface-card resource-step-card">
                  <div className="homepage-step-number">0{index + 1}</div>
                  <h3 className="homepage-card-title">{item.title}</h3>
                  <p className="homepage-card-copy">{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">How to use it</span>
            <h2 className="homepage-section-title resource-section-title">Use the template alongside the guide and checklist</h2>
            <ol className="resource-numbered-list">
              {rolloutSteps.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt">
          <div className="homepage-shell homepage-final-cta-shell resource-final-shell">
            <div>
              <span className="homepage-label-chip">Next action</span>
              <h2 className="homepage-section-title resource-section-title">Want the full operating model too?</h2>
              <p className="homepage-section-body resource-section-body">
                Use the guide for the full system, the checklist for the shortest execution layer, and the template when you need a practical asset managers can work from immediately.
              </p>
            </div>
            <div className="resource-final-actions">
              <Link to={MENU_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
              <Link to={MENU_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open checklist</Link>
              <a href={checklistUrl} className="resource-tertiary-link">Share the checklist URL</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}